import { memo, useCallback, useRef, useLayoutEffect, useEffect, useState, type MouseEvent } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Project, ProjectFlagStage, UpdateProjectData } from '@/api/client';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  captureProjectListRollback,
  mergeSingleProjectInCaches,
  patchProjectListCaches,
  type ProjectListRollbackContext,
} from '@/lib/projectListQueryCache';
import { getProjectEntityFromCaches } from '@/lib/projectEntityCache';
import { runAfterPendingRowPatches } from '@/lib/projectRowPatchQueue';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import { useToast } from '@/contexts/ToastContext';
import {
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import '@/components/ui/ContextMenu/ContextMenu.css';
import '@/components/ProjectsTable/TableContextMenu.css';
import clsx from 'clsx';
import './StatusButtonCell.css';

export type StatusButtonFieldId =
  | 'project_phonogram'
  | 'project_dp'
  | 'project_approved'
  | 'project_done'
  | 'project_pdf';


const STATUS_BUTTON_TOAST_LABEL: Record<StatusButtonFieldId, string> = {
  project_phonogram: 'Ф',
  project_dp: 'Ц',
  project_approved: 'С',
  project_done: 'D',
  project_pdf: 'PDF',
};

type StatusMenuOption = {
  key: string;
  label: string;
  value: ProjectFlagStage;
  dotClassName: string;
};

/** Стадии отметки (миграция 179): цвета — как в новом дизайне, серая — «Не выбрано» */
const STATUS_MENU_OPTIONS: StatusMenuOption[] = [
  { key: 'none', label: 'Не выбрано', value: 0, dotClassName: 'projects-table__status-menu-dot_empty' },
  { key: 'red', label: 'Красный', value: 1, dotClassName: 'projects-table__status-menu-dot_red' },
  { key: 'yellow', label: 'Жёлтый', value: 2, dotClassName: 'projects-table__status-menu-dot_yellow' },
  { key: 'green', label: 'Зелёный', value: 3, dotClassName: 'projects-table__status-menu-dot_green' },
];

export interface StatusButtonCellProps {
  project: Project;
  fieldId: StatusButtonFieldId;
  label: string;
  title?: string;
  disabled?: boolean;
}

function valuesEqual(a: ProjectFlagStage | undefined, b: ProjectFlagStage | undefined): boolean {
  return (a ?? 0) === (b ?? 0);
}

function buildUpdateData(fieldId: StatusButtonFieldId, next: ProjectFlagStage): UpdateProjectData {
  return { [fieldId]: next };
}

function patchProjectsField(
  prev: Project[] | undefined,
  projectId: string,
  fieldId: StatusButtonFieldId,
  next: ProjectFlagStage
): Project[] | undefined {
  if (!prev) return prev;
  return prev.map((p) => (p.id === projectId ? { ...p, [fieldId]: next } : p));
}

const STAGE_VARIANT = ['outline', 'red', 'yellow', 'green'] as const;

function getButtonVariant(value: ProjectFlagStage | undefined): (typeof STAGE_VARIANT)[number] {
  return STAGE_VARIANT[value ?? 0] ?? 'outline';
}

function readFieldFromProjectCaches(
  queryClient: QueryClient,
  projectId: string,
  fieldId: StatusButtonFieldId
): ProjectFlagStage | undefined {
  const project = getProjectEntityFromCaches(queryClient, projectId);
  return project?.[fieldId] ?? undefined;
}

export const StatusButtonCell = memo(function StatusButtonCell({
  project,
  fieldId,
  label,
  title: titleProp,
  disabled,
}: StatusButtonCellProps) {
  const title = titleProp ?? label;
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const [optimisticValue, setOptimisticValue] = useState<ProjectFlagStage | undefined>(undefined);

  const logicalValueRef = useRef<ProjectFlagStage | undefined>(project[fieldId]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setStatusMenuOpen(false);
  }, [project.id, fieldId]);

  const propValue = project[fieldId];
  /**
   * Пока висит локальный optimistic — НЕ перезаписывать logicalValueRef из propValue:
   * иначе после setOptimisticValue эффект срабатывает до microtask с flushOneRound и
   * стирает «next», want совпадает с кэшем → PATCH не уходит (кнопка «не жмётся»).
   */
  useLayoutEffect(() => {
    if (optimisticValue !== undefined) {
      if (valuesEqual(optimisticValue, propValue)) {
        setOptimisticValue(undefined);
      }
      return;
    }
    logicalValueRef.current = propValue;
  }, [project.id, fieldId, propValue, optimisticValue]);

  const projectIdRef = useRef(project.id);
  projectIdRef.current = project.id;

  const fieldIdRef = useRef(fieldId);
  fieldIdRef.current = fieldId;

  const displayValue = optimisticValue !== undefined ? optimisticValue : propValue;
  const variant = getButtonVariant(displayValue);
  const note = project[`${fieldId}_note`]?.trim();

  const flushOneRound = useCallback(async (): Promise<void> => {
    const pid = projectIdRef.current;
    const fid = fieldIdRef.current;
    const qc = queryClient;

    while (true) {
      const want = logicalValueRef.current ?? 0;
      const inCache = readFieldFromProjectCaches(qc, pid, fid);

      if (valuesEqual(want, inCache)) {
        if (mountedRef.current) setOptimisticValue(undefined);
        return;
      }

      const rollback: ProjectListRollbackContext = captureProjectListRollback(qc);
      patchProjectListCaches(qc, (prev) => patchProjectsField(prev, pid, fid, want));
      markProjectUpdatedByMutation(pid);

      try {
        const data = await api.projects.update(pid, buildUpdateData(fid, want));
        mergeSingleProjectInCaches(qc, data);
        setLastUndoRedoProjectId(data.id);
        void refreshUndoRedoAvailability(qc, data.id).catch(() => {});
        const got = data[fid];
        const wantAfter = logicalValueRef.current ?? 0;

        if (valuesEqual(got, wantAfter)) {
          if (mountedRef.current) setOptimisticValue(undefined);
          showSuccess(`${STATUS_BUTTON_TOAST_LABEL[fid]}: сохранено`);
          return;
        }
      } catch (err) {
        void rollbackProjectsAndReconcile(qc, rollback).catch(() => {});
        showError(err instanceof Error ? err.message : String(err));
        if (mountedRef.current) {
          logicalValueRef.current = readFieldFromProjectCaches(qc, pid, fid);
          setOptimisticValue(undefined);
        }
        return;
      }
    }
  }, [queryClient, showSuccess, showError]);

  const commitValue = useCallback((next: ProjectFlagStage) => {
    if (disabled) return;

    logicalValueRef.current = next;
    setOptimisticValue(next);

    runAfterPendingRowPatches(project.id, flushOneRound);
  }, [disabled, flushOneRound, project.id]);

  const statusMenuOptions = STATUS_MENU_OPTIONS;

  const closeStatusMenu = useCallback(() => {
    setStatusMenuOpen(false);
  }, []);

  const handleStatusButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      e.stopPropagation();
      setStatusMenuOpen((o) => !o);
    },
    [disabled]
  );

  const handleStatusMenuSelect = useCallback(
    (next: ProjectFlagStage) => {
      commitValue(next);
      setStatusMenuOpen(false);
    },
    [commitValue]
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={clsx('projects-table__status-btn', `projects-table__status-btn_${variant}`)}
        onClick={handleStatusButtonClick}
        disabled={disabled}
        aria-label={`${title}: ${variant}`}
        // Комментарий отметки (новый дизайн) — второй строкой подсказки
        title={note ? `${title}\n${note}` : title}
        aria-haspopup="menu"
        aria-expanded={statusMenuOpen}
      >
        {label}
      </button>
      <ContextMenu
        isOpen={statusMenuOpen}
        onClose={closeStatusMenu}
        anchorPosition={null}
        anchorEl={statusMenuOpen ? btnRef.current : null}
        anchorPriorityAxis="y"
        excludeRefs={[btnRef]}
        repositionOnScroll={false}
      >
        <ul
          className="context-menu__list table-context-menu__list"
          role="menu"
          aria-label={`${title}: выбор статуса`}
        >
          {statusMenuOptions.map((option) => {
            const selected = valuesEqual(displayValue, option.value);
            return (
              <li key={option.key} className="context-menu__list-item" role="none">
                <button
                  type="button"
                  className={clsx('context-menu__item', selected && 'context-menu__item_selected')}
                  role="menuitemradio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => handleStatusMenuSelect(option.value)}
                >
                  <span className="context-menu__iconCell" aria-hidden>
                    <span className={clsx('projects-table__status-menu-dot', option.dotClassName)} />
                  </span>
                  <span className="context-menu__item-text">{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </ContextMenu>
    </>
  );
});
